// src/pages/Lesson.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Star,  Clock, User, MapPin, CheckCircle, AlertCircle, Loader, Edit3, MessageCircle, XCircle, Check, X, UserX} from 'lucide-react';

import { Lesson, LessonAction } from '../components/interfaces/All';
import { useSupabase } from '../context/SupabaseContext';

const LessonPage: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { state, fetchLessons, cancelLesson, completeLesson, rescheduleLesson, markNoShowLesson } = useSupabase();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ensure lessons are loaded, then find lesson
  useEffect(() => {
    const load = async () => {
      try {
        if (!state.lessons || state.lessons.length === 0) {
          await fetchLessons();
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load lesson.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fetchLessons, state.lessons]);

  const lesson = useMemo(
    () => state.lessons.find((l) => l.id === lessonId),
    [state.lessons, lessonId],
  );

  const getStatusIcon = (status: LessonAction) => {
    if (status === 'completed') return <Check className="w-3 h-3" />;
    if (status === 'no-show') return <UserX className="w-3 h-3" />;
    if (status === 'reschedule') return <Clock className="w-3 h-3" />;
    if (status === 'canceled') return <XCircle className="w-3 h-3" />;
    if (status === 'confirmed') return <CheckCircle className="w-3 h-3" />;
    return null;
  };
  function getStatusColor(status: LessonAction): string {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'no-show':
        return 'bg-gray-200 text-gray-800';
      case 'reschedule':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancel':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6 flex items-center justify-center h-64">
        <Loader className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card p-6 text-center">
          <h2 className="text-lg font-semibold mb-2">Lesson not found</h2>
          <p className="text-text-light mb-4">The lesson you are looking for does not exist.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/lesson')}
          >
            Go to Lessons
          </button>
        </div>
      </div>
    );
  }

  const dateObj = new Date(lesson.time);
  const dateLabel = dateObj.toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timeLabel = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });


  return (
    <div className="max-w-2xl mx-auto p-1 space-y-6">

      <div className="card p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="text-sm text-text-light">{dateLabel}</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-primary" />
              <span className="text-lg font-semibold">{timeLabel}</span>
              <span className="text-xs text-text-light ml-2">
                {lesson.duration} min
              </span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mt-1 border border-border">


              {/* Status label */}
              {getStatusIcon(lesson.status)}
              <span className={getStatusColor(lesson.status).split(' ')[0]}>
                {lesson.status}
              </span>

            </div>
          </div>
        </div>

        {/* Student & Location */}
        <div className="space-y-2 pt-4 border-t border-border">
          <div className='flex gap-2'>

            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-text-light" />
              <span className="font-semibold">{lesson.student}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-text-light" />
              <span className="font-semibold">{lesson.teacher}</span>
            </div>

          </div>
          <div className="flex items-center gap-2 text-sm text-text-light">
            <MapPin className="w-4 h-4" />
            <span>{lesson.location}</span>
          </div>
        </div>

        {/* Notes */}
        <div className="pt-4 border-t border-border space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Notes</span>
            <button
              className="text-xs flex items-center gap-1 text-primary"
              onClick={() => navigate(`/lesson/${lesson.id}/edit`)}
            >
              <Edit3 className="w-3 h-3" />
              Edit
            </button>
          </div>
          {lesson.notes ? (
            <p className="text-sm text-text-light bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg whitespace-pre-wrap">
              {lesson.notes}
            </p>
          ) : (
            <p className="text-sm text-text-light italic">
              No notes yet. Click "Edit" to add notes.
            </p>
          )}
        </div>



        {/* Notes and feedback */}
        { true && (
          <div className="pt-4 border-t border-border space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Feedback from teacher</span>
            </div>
            {lesson.teacher_feedback ? (
              <>
                <div className="flex items-center gap-1">
                  {Array.from({ length: lesson.teacher_rating }, (_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-text-light bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg whitespace-pre-wrap">
                  "{lesson.teacher_feedback}"
                </p>
              </>
            ) : (
              <p className="text-sm text-text-light italic">
                No feedback from the teacher yet.
              </p>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Feedback from student</span>
            </div>
            {lesson.student_feedback ? (
              <>
                <div className="flex items-center gap-1">
                  {Array.from({ length: lesson.student_rating }, (_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-text-light bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg whitespace-pre-wrap">
                  "{lesson.student_feedback}"
                </p>
              </>
            ) : (
              <p className="text-sm text-text-light italic">
                No feedback from the student yet.
              </p>
            )}
          </div>
        ) }

        {/* Actions */}
        {state.userRole === 'teacher' && (<>
          <div className="pt-4 border-t border-border flex flex-col gap-2 sm:flex-row sm:gap-3">

            <button
              className="btn btn-primary flex justify-center"
              // disabled={ ['canceled', 'no-show', 'completed'].includes(lesson.status) || actionLoading }
              onClick={() => navigate(`/lesson/${lesson.id}/feedback`)}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Mark Completed
            </button>

            <button
              className="btn btn-outline flex justify-center gap-2 border-red-200 dark:border-red-800 dark:hover:bg-red-900/20"
              // disabled={ ['canceled', 'no-show', 'completed'].includes(lesson.status) || actionLoading }
              onClick={async ()=>{
                setActionLoading(true);
                await markNoShowLesson(lesson.id);
                setActionLoading(false);
              }}
            >
              <UserX className="w-4 h-4 mr-1" />
              Mark no-show
            </button>

            <button
              className="btn btn-outline flex justify-center gap-2 border-blue-200 dark:border-blue-800 dark:hover:bg-blue-900/20"
              // disabled={ ['canceled', 'no-show', 'completed'].includes(lesson.status) || actionLoading }
              onClick={async ()=>{
                setActionLoading(true);
                await rescheduleLesson(lesson.id);
                setActionLoading(false);
              }}
            >
              <Clock className="w-4 h-4 mr-1" />
              Mark Rescheduled
            </button>

            <button
              className={`btn btn-outline flex justify-center gap-2 border-red-200 dark:border-red-800 dark:hover:bg-red-900/20 ${
                  ['canceled', 'no-show', 'completed'].includes(lesson.status) ? 'opacity-50 cursor-not-allowed text-gray-600' : ' text-red-600'
              }`}
              // disabled={ ['canceled', 'no-show', 'completed'].includes(lesson.status) || actionLoading }
              onClick={async ()=>{
                setActionLoading(true);
                await cancelLesson(lesson.id);
                setActionLoading(false);
              }}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Cancel Lesson
            </button>

          </div>
        </>)}


        {error && (
          <p className="text-xs text-red-500 mt-2">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default LessonPage;
